import { memo, useRef, useState } from 'react';
import { Send, Loader2, Image as ImageIcon } from 'lucide-react';

const CHANGE_TYPES = [
  { key: 'any', label: 'Any' },
  { key: 'copy', label: 'Copy' },
  { key: 'style', label: 'Style' },
  { key: 'structure', label: 'Layout' },
];

const ChatTab = memo(({ chatHistory, fullChat, setFullChat, sending, onSubmit, activePageName, t, theme, inputStyle }) => {
  const chatEndRef = useRef(null);
  const [changeType, setChangeType] = useState('any');

  const handleScopedSubmit = (e) => {
    if (typeof e?.preventDefault === 'function') e.preventDefault();
    const msg = typeof e === 'string' ? e : fullChat.trim();
    if (!msg || sending) return;
    let prefix = '';
    if (changeType === 'copy') prefix = 'CONSTRAINT: Only change text/copy. Do NOT change CSS, layout, colors, fonts, or images. ';
    else if (changeType === 'style') prefix = 'CONSTRAINT: Only change styling (CSS, colors, spacing, fonts). Do NOT change text or images. ';
    else if (changeType === 'structure') prefix = 'CONSTRAINT: Change the layout/structure. Rearrange sections, change grids, restructure blocks. ';

    setFullChat(prefix + (typeof e === 'string' ? e : fullChat));
    setTimeout(() => { onSubmit(typeof e === 'string' ? e : undefined); setFullChat(''); }, 10);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scope chips */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {CHANGE_TYPES.map(ct => (
          <button key={ct.key} onClick={() => setChangeType(ct.key)} style={{
            padding: '5px 12px', fontSize: '11px', fontFamily: 'var(--sans)', fontWeight: changeType === ct.key ? 500 : 400,
            color: changeType === ct.key ? t.fg : t.muted,
            background: changeType === ct.key ? (theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)') : 'transparent',
            border: 'none', cursor: 'pointer', borderRadius: '20px',
            boxShadow: changeType === ct.key ? (theme === 'light' ? '0 1px 3px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.3)') : 'none',
            transition: 'all .15s',
          }}>
            {ct.label}
          </button>
        ))}
      </div>

      {/* Chat history */}
      <div data-testid="chat-history" style={{ flex: 1, overflowY: 'auto', marginBottom: '10px' }}>
        {chatHistory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '28px 12px' }}>
            <p style={{ fontFamily: 'var(--serif)', fontSize: '16px', color: t.fg, marginBottom: '6px' }}>Hey, I'm Iris.</p>
            <p style={{ fontSize: '13px', color: t.muted, lineHeight: 1.6, marginBottom: '14px', fontFamily: 'var(--sans)' }}>
              Describe what you'd like to change, or ask me anything about your site.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
              {['Make the hero more cinematic', 'Add parallax scrolling', 'Rewrite the about section', 'Make it feel more luxury'].map(s => (
                <button key={s} onClick={() => setFullChat(s)} style={{
                  padding: '6px 14px', fontSize: '11px', fontFamily: 'var(--sans)',
                  color: t.gold, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)',
                  cursor: 'pointer', transition: 'all .15s', borderRadius: '20px',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                >{s}</button>
              ))}
            </div>
          </div>
        )}
        {chatHistory.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '10px' }}>
            <div style={{
              maxWidth: '85%', padding: '10px 14px', fontSize: '13px', fontFamily: 'var(--sans)', lineHeight: 1.6,
              background: msg.role === 'user' ? t.chatUser : t.chatAi,
              color: msg.role === 'user' ? t.chatUserText : t.chatAiText,
              borderRadius: '14px',
              userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text',
            }}>
              {msg.message}
              {msg.page && msg.role === 'user' && (
                <span style={{ display: 'block', fontSize: '10px', marginTop: '4px', opacity: 0.5 }}>{msg.page} page</span>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
            <div style={{ padding: '10px 14px', background: t.chatAi, borderRadius: '14px', fontSize: '13px', color: t.muted, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 size={13} className="animate-spin" style={{ color: t.gold }} /> Iris is on it...
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleScopedSubmit} style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <input
          type="text" value={fullChat} onChange={e => setFullChat(e.target.value)}
          placeholder={changeType === 'copy' ? 'Describe the copy change...' : changeType === 'style' ? 'Describe the style change...' : 'Tell Iris what to change...'}
          disabled={sending}
          data-testid="chat-input"
          style={{ ...inputStyle, borderRadius: '10px' }}
        />
        <button type="submit" disabled={sending || !fullChat.trim()} data-testid="chat-send-btn" style={{
          width: '38px', height: '38px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: fullChat.trim() ? t.gold : t.faint, color: fullChat.trim() ? '#000' : t.muted,
          border: 'none', cursor: fullChat.trim() ? 'pointer' : 'default', borderRadius: '10px', transition: 'all .15s',
        }}>
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
});

ChatTab.displayName = 'ChatTab';
export default ChatTab;
